/* Viewport model for the visibility walk: given a document, captures its viewport size,
 * scroll offset and per-axis scrollable extent, and answers whether a rect can be brought
 * on-screen by scrolling (`canScrollIntoView`, document coords) or, for fixed elements that
 * never scroll, whether it's on-screen right now (`isInView`, viewport coords).
 * One instance captures this document's bounds. Should be cleared on each run. */

import type { MaybeNull } from "@protontech/autofill/types/utils";

class Viewport {
  /** Document-coordinate bounds the near edges must fall within to
   * be scrollable into view. Each collapses to the viewport on an axis
   * that can't scroll (see the constructor). */
  private readonly maxRight: number;
  private readonly maxBottom: number;
  private readonly scrollX: number;
  private readonly scrollY: number;

  /** Viewport size excluding scrollbars (the `isInView` bounds). */
  private readonly viewportWidth: number;
  private readonly viewportHeight: number;

  constructor(win: Window, doc: Document) {
    /** `clientWidth/Height` measure the viewport excluding scrollbars,
     * wherever the browser draws them (classic, overlay, RTL/left-side). */
    this.viewportWidth = doc.documentElement.clientWidth;
    this.viewportHeight = doc.documentElement.clientHeight;

    /** Viewport overflow propagates from <html>, falling back to <body>
     * only when <html>'s computed overflow is `visible` (CSS spec). An axis
     * whose overflow is `hidden`/`clip` cannot be scrolled into view, so the
     * root bound collapses to the viewport. Using scroll{Width,Height} there
     * would let an off-screen field inflate the bound and pass its own check,
     * the occlusion honeypot this guards against. */
    const rootStyle = win.getComputedStyle(doc.documentElement);
    const bodyStyle = win.getComputedStyle(doc.body);
    const overflowX = rootStyle.overflowX === "visible" ? bodyStyle.overflowX : rootStyle.overflowX;
    const overflowY = rootStyle.overflowY === "visible" ? bodyStyle.overflowY : rootStyle.overflowY;
    const scrollableX = overflowX !== "hidden" && overflowX !== "clip";
    const scrollableY = overflowY !== "hidden" && overflowY !== "clip";

    this.maxRight = scrollableX ? Math.max(this.viewportWidth, doc.documentElement.scrollWidth, doc.body.scrollWidth) : this.viewportWidth;
    this.maxBottom = scrollableY ? Math.max(this.viewportHeight, doc.documentElement.scrollHeight, doc.body.scrollHeight) : this.viewportHeight;
    this.scrollX = win.scrollX;
    this.scrollY = win.scrollY;
  }

  /** Document coordinates: can the root scroller bring this rect into view?
   * Bounds are strict: visible means at least ONE visible pixel. Each check
   * compares an edge against the OPPOSITE boundary (`right` against the left
   * boundary, `left` against the right one), so an element inside an edge (eg
   * `top: 0; left: 0`) passes with its full width/height of overlap, while one
   * outside (eg: an element at `translateX(±100%)`, where `right == 0` or
   * `left == maxRight`) has zero-pixel overlap and is off-screen. */
  canScrollIntoView({ x, y, width, height }: DOMRect): boolean {
    const left = x + this.scrollX;
    const right = x + this.scrollX + width;
    const top = y + this.scrollY;
    const bottom = y + this.scrollY + height;

    return right > 0 && left < this.maxRight && bottom > 0 && top < this.maxBottom;
  }

  /** Viewport coordinates: fixed elements never scroll, they're
   * reachable only if on screen right now. Same strict
   * one-visible-pixel bounds as `canScrollIntoView`. */
  isInView({ x, y, width, height }: DOMRect): boolean {
    return x + width > 0 && x < this.viewportWidth && y + height > 0 && y < this.viewportHeight;
  }
}

let cache: MaybeNull<Viewport> = null;
export const clearViewportCache = () => (cache = null);
export const getViewport = (win: Window, doc: Document): Viewport => (cache ??= new Viewport(win, doc));
export type { Viewport };
